import { z } from 'zod';

import {
  hostnameSchema,
  nonNegativeIntSchema,
  portSchema,
  positiveIntSchema,
  slugSchema,
  uuidSchema,
} from '../common/primitives';
import { absolutePathSchema } from '../fleet/manifest';

/**
 * Der Aufbewahrungsort der Sicherungen: eine Hetzner Storage Box.
 *
 * Bewusst SFTP und nicht S3. Eine Storage Box ist der Ort, an dem die
 * Sicherungen eines Hosters ohnehin liegen; ein zusätzlicher Objektspeicher
 * wäre ein zweiter Anbieter, ein zweites Abrechnungsmodell und ein zweiter Weg,
 * auf dem Zugangsdaten verloren gehen können. `restic` spricht SFTP unmittelbar.
 *
 * ## Was hier NICHT steht
 *
 * Der SSH-Schlüssel der Box. Er gehört dem Node und nicht der Site und reist
 * über das Manifest aus M4b auf jeden Worker — derselbe Weg, über den
 * `firewall-edges.env` schon heute verteilt wird. In diesem Schema stünde er in
 * jeder API-Antwort, die eine Box beschreibt.
 */

/**
 * Der SSH-Port einer Hetzner Storage Box.
 *
 * 23 und nicht 22: Hetzner betreibt auf Port 22 nur den eingeschränkten
 * Zugang ohne SFTP-Unterstützung für Unterkonten. Wer hier 22 einträgt, bekommt
 * eine Verbindung, die sich aufbaut und danach jede Dateioperation verweigert —
 * ein Fehlerbild, das nach einem Rechteproblem aussieht und keines ist.
 */
export const STORAGE_BOX_DEFAULT_PORT = 23;

/**
 * Benutzername einer Storage Box, z. B. `u123456` oder `u123456-sub1`.
 *
 * Eng gefasst, weil dieser Wert in die Repository-Adresse wandert und die
 * Repository-Adresse ein Argument von `restic` wird.
 */
export const storageBoxUsernameSchema = z.string().regex(/^[a-z\d][a-z\d-]{0,31}$/, {
  message:
    'Nur Kleinbuchstaben, Ziffern und Bindestriche; muss mit Buchstabe oder Ziffer beginnen.',
});

export const storageBoxSchema = z.object({
  id: uuidSchema,
  /** Sprechender Name für den Betreiber, z. B. `bx11-fsn1`. */
  label: slugSchema,
  host: hostnameSchema,
  port: portSchema,
  username: storageBoxUsernameSchema,
  /** Verzeichnis auf der Box, unterhalb dessen je Site ein Repository entsteht. */
  basePath: absolutePathSchema,
  capacityGb: positiveIntSchema,
  /**
   * Belegung, wie der Betreiber sie zuletzt eingetragen oder der Agent sie
   * gemeldet hat. Getrennt von der Reservierung geführt — dieselbe Trennung von
   * Soll und Ist wie bei der Kapazität eines Nodes.
   */
  usedGb: nonNegativeIntSchema,
  /**
   * `false` nimmt die Box aus der Auswahl, ohne die Sites zu verlieren, die
   * schon auf ihr liegen. Genau das ist der Unterschied zwischen "keine neuen
   * mehr" und "ab jetzt keine Sicherungen mehr".
   */
  enabled: z.boolean(),
});

export type StorageBox = z.infer<typeof storageBoxSchema>;

// -- Die Repository-Adresse --------------------------------------------------

/**
 * Die Adresse eines restic-Repositories.
 *
 * **Dieser Wert wird zu einem Argument von `restic`** (`--repo=<wert>`) und ist
 * damit die einzige Zeichenkette, die der Master in eine Argumentliste des
 * Agents einbringt. Deshalb steht hier eine Positivliste und keine Prüfung auf
 * verbotene Zeichen: Erlaubt sind genau zwei Formen, alles andere fällt durch.
 *
 *   - `local:/srv/kraaapf/backup/<siteId>`        — für Tests und Entwicklung
 *   - `sftp:u123456@u123456.your-storagebox.de:/sites/<siteId>`
 *
 * Kein `rest:`, kein `s3:`, kein `rclone:` — `rclone:` insbesondere nicht,
 * weil restic dafür ein beliebiges Programm startet.
 */
export const resticRepositorySchema = z
  .string()
  .min(8)
  .max(255)
  .regex(/^(?:local:\/[\w./-]+|sftp:[a-z\d][a-z\d-]{0,31}@[a-z\d][a-z\d.-]{0,251}:\/[\w./-]+)$/, {
    message: 'Erlaubt sind ausschließlich `local:/<pfad>` und `sftp:<benutzer>@<host>:/<pfad>`.',
  })
  .refine((value) => !value.includes('..'), {
    message: 'Ein Repository-Pfad darf keinen Verzeichniswechsel nach oben enthalten.',
  });

export type ResticRepository = z.infer<typeof resticRepositorySchema>;

/**
 * Das Passwort eines Repositories.
 *
 * Derselbe Zeichenvorrat wie bei den übrigen Kundengeheimnissen
 * (`accessPasswordSchema`), aber eigens definiert: Dieses Passwort wird nie in
 * ein `GRANT` eingesetzt und muss deshalb nicht denselben Einschränkungen
 * folgen — es soll ihnen nur nicht widersprechen, damit es überall gefahrlos
 * durchgereicht werden kann.
 */
export const resticPasswordSchema = z.string().regex(/^[A-Za-z\d._~-]{24,64}$/, {
  message: 'Muss 24 bis 64 Zeichen aus [A-Za-z0-9._~-] enthalten.',
});

/**
 * Bildet die Repository-Adresse einer Site auf einer Box.
 *
 * Rein und ohne Uhr: Dieselbe Site auf derselben Box ergibt immer dieselbe
 * Adresse. Der Pfad endet auf der Site-ID und nicht auf einem sprechenden Namen
 * — ein Kunde, der seine Site umbenennt, soll sein Repository behalten.
 */
export function buildResticRepository(
  box: Pick<StorageBox, 'username' | 'host' | 'basePath'>,
  siteId: string,
): string {
  const base = box.basePath.endsWith('/') ? box.basePath.slice(0, -1) : box.basePath;

  return `sftp:${box.username}@${box.host}:${base}/${siteId}`;
}
